// TODO: vitest component test (pages are covered by Playwright e2e)
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Badge, Button, Card, Input, Radio, Select, Textarea, SearchableDropdown, FormActions } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { useVehicle, useCreateVehicle, useUpdateVehicle } from '@/hooks/useVehicles'
import { useProperty } from '@/hooks/useProperties'
import { useCustomers, useCustomer } from '@/hooks/useCustomers'
import { useLeases, useLease } from '@/hooks/useLeases'
import { THAI_PROVINCES } from '@/constants/thaiProvinces'
import i18n from '@/i18n'
import type { Customer, Lease } from '@/types'
import type { VehicleType, ParkerType } from '@/types/parking'
import {
  VehiclePhotoUpload,
  vehiclePhotoUrl,
  uploadVehiclePhoto,
} from '@/components/vehicles/VehiclePhotoUpload'

interface VehicleFormData {
  plate_text: string
  license_plate: string
  vehicle_type: VehicleType | ''
  province: string
  brand: string
  model: string
  color: string
  parker_type: ParkerType
  // Tenant zone
  lease_id: string
  // External zone
  customer_id: string
  notes: string
}

export function VehicleFormPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const { propertyId: propertyIdParam, id } = useParams<{
    propertyId?: string
    id?: string
  }>()
  const isEditing = !!id
  const vehicleId = Number(id)
  const navigate = useNavigate()

  const { data: existingVehicle, isLoading: isLoadingVehicle } = useVehicle(
    isEditing ? vehicleId : 0
  )

  // For new vehicles use propertyId from route; for edits derive it from the loaded vehicle
  const resolvedPropertyId = isEditing
    ? existingVehicle?.property_id
    : Number(propertyIdParam)

  const { data: property } = useProperty(resolvedPropertyId ?? 0)

  // Server-side search query for the lease dropdown (mirrors InvoiceFields pattern).
  const [leaseSearch, setLeaseSearch] = useState('')
  const handleLeaseSearch = useCallback((query: string) => {
    setLeaseSearch(query)
  }, [])

  // Leases filtered to active + pending_start for the tenant dropdown,
  // scoped to the current property so cross-property leases don't pollute.
  // onSearch wires the query to the `q` param so the server searches unit_number
  // (and other indexed fields). EP-116 T008: paged + accumulated past page 1,
  // same shape as the customer picker below / InvoiceFields' lease picker.
  const [leasePage, setLeasePage] = useState(1)
  const [accumulatedLeases, setAccumulatedLeases] = useState<Lease[]>([])
  const { data: activeLeasesData, isLoading: isLoadingLeases } = useLeases(
    leasePage,
    'active,pending_start',
    !!resolvedPropertyId,
    leaseSearch || undefined,
    resolvedPropertyId,
  )

  // A new query (or a change of scoped property) starts a new list; anything
  // else appends the next page.
  useEffect(() => {
    setLeasePage(1)
    setAccumulatedLeases([])
  }, [leaseSearch, resolvedPropertyId])

  useEffect(() => {
    if (!activeLeasesData?.data) return
    setAccumulatedLeases((prev) => {
      if (leasePage === 1) return activeLeasesData.data
      const existingIds = new Set(prev.map((l) => l.id))
      return [...prev, ...activeLeasesData.data.filter((l) => !existingIds.has(l.id))]
    })
  }, [activeLeasesData, leasePage])
  const hasMoreLeases =
    !!activeLeasesData?.meta && activeLeasesData.meta.current_page < activeLeasesData.meta.total_pages

  // Edit mode: if the existing vehicle's lease_id is not in the loaded/searched
  // pages (because the property has >20 active leases, or the lease sits past
  // whatever page has been loaded so far), fetch it directly so the preselected
  // option is always present in the dropdown. `activeLeaseIds` covers every
  // ACCUMULATED page, not just the current one, so this correctly turns off
  // once pagination reaches the real lease (T008 — same fix as InvoiceFields).
  const editLeaseId = isEditing && existingVehicle?.lease_id
    ? existingVehicle.lease_id
    : 0
  const activeLeaseIds = new Set(accumulatedLeases.map((l) => l.id))
  const needFallbackLease = !!editLeaseId && !activeLeaseIds.has(editLeaseId)
  const { data: fallbackLease } = useLease(editLeaseId, needFallbackLease)
  const createVehicle = useCreateVehicle()
  const updateVehicle = useUpdateVehicle()

  // Customer picker (external parker): server search + page accumulation
  // (EP-116 T006), mirroring ParcelFormPage. SearchableDropdown fires
  // onSearch('') when opened, so page 1 loads a recent-customers browse list
  // before the user types anything — replaces the old two-hook
  // browse-list-vs-searched-list split, which never paginated either branch.
  const [customerSearchQuery, setCustomerSearchQuery] = useState('')
  const [customerPage, setCustomerPage] = useState(1)
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<Customer[]>([])
  const { data: customersData, isLoading: isLoadingCustomers } = useCustomers(
    customerPage, undefined, customerSearchQuery || undefined,
  )

  // A new query starts a new list; anything else appends the next page.
  useEffect(() => {
    setCustomerPage(1)
    setAccumulatedCustomers([])
  }, [customerSearchQuery])

  useEffect(() => {
    if (!customersData?.data) return
    setAccumulatedCustomers((prev) => {
      if (customerPage === 1) return customersData.data
      const existingIds = new Set(prev.map((c) => c.id))
      return [...prev, ...customersData.data.filter((c) => !existingIds.has(c.id))]
    })
  }, [customersData, customerPage])

  // Edit mode: unlike the lease picker (locked in some other forms), the
  // customer picker here has NO isEditing gate — it's unconditionally
  // editable (BA, 2026-08-05) — so a saved customer outside the
  // loaded/searched page needs the same fallback-fetch guard as the fallback
  // lease above, or an unrelated save would silently retarget the vehicle's
  // customer.
  const editCustomerId = isEditing && existingVehicle?.customer_id
    ? existingVehicle.customer_id
    : 0
  const accumulatedCustomerIds = new Set(accumulatedCustomers.map((c) => c.id))
  const needFallbackCustomer = !!editCustomerId && !accumulatedCustomerIds.has(editCustomerId)
  const { data: fallbackCustomer } = useCustomer(needFallbackCustomer ? editCustomerId : 0)

  const provinceOptions = THAI_PROVINCES.map((p) => ({
    value: p.value,
    label: i18n.language === 'th' ? p.labelTh : p.labelEn,
  }))

  // Merge the accumulated pages with the fallback customer (edit mode,
  // customer outside the loaded/searched page).
  const customers = (() => {
    if (needFallbackCustomer && fallbackCustomer) {
      return [fallbackCustomer, ...accumulatedCustomers]
    }
    return accumulatedCustomers
  })()

  // Merge the accumulated pages with the fallback lease (edit mode, lease
  // outside the loaded/searched pages).
  const leases = (() => {
    if (needFallbackLease && fallbackLease) {
      return [fallbackLease, ...accumulatedLeases]
    }
    return accumulatedLeases
  })()

  const vehicleTypeOptions = [
    { value: '', label: `— ${tc('actions.filter')} —` },
    { value: 'car', label: t('vehicles.types.car') },
    { value: 'motorcycle', label: t('vehicles.types.motorcycle') },
    { value: 'pickup', label: t('vehicles.types.pickup') },
    { value: 'bicycle', label: t('vehicles.types.bicycle') },
    { value: 'van', label: t('vehicles.types.van') },
  ]

  // ---------------------------------------------------------------------------
  // P0-01: photo key state (bare UUID keys, not /api/images/... paths)
  // ---------------------------------------------------------------------------
  const [frontPhotoKey, setFrontPhotoKey] = useState<string | null>(null)
  const [rearPhotoKey, setRearPhotoKey] = useState<string | null>(null)
  const [frontPhotoUploading, setFrontPhotoUploading] = useState(false)
  const [rearPhotoUploading, setRearPhotoUploading] = useState(false)
  const [frontPhotoError, setFrontPhotoError] = useState<string | undefined>()
  const [rearPhotoError, setRearPhotoError] = useState<string | undefined>()
  // Track whether user attempted to submit (to show photo errors)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const frontPhotoInputRef = useRef<HTMLInputElement>(null)
  const rearPhotoInputRef = useRef<HTMLInputElement>(null)

  const handleFrontPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFrontPhotoUploading(true)
    setFrontPhotoError(undefined)
    try {
      const key = await uploadVehiclePhoto(file)
      setFrontPhotoKey(key)
    } catch {
      setFrontPhotoError(t('vehicles.photo_upload_error'))
    } finally {
      setFrontPhotoUploading(false)
      if (frontPhotoInputRef.current) frontPhotoInputRef.current.value = ''
    }
  }

  const handleRearPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setRearPhotoUploading(true)
    setRearPhotoError(undefined)
    try {
      const key = await uploadVehiclePhoto(file)
      setRearPhotoKey(key)
    } catch {
      setRearPhotoError(t('vehicles.photo_upload_error'))
    } finally {
      setRearPhotoUploading(false)
      if (rearPhotoInputRef.current) rearPhotoInputRef.current.value = ''
    }
  }

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useForm<VehicleFormData>({
    defaultValues: {
      plate_text: '',
      license_plate: '',
      vehicle_type: '',
      province: '',
      brand: '',
      model: '',
      color: '',
      parker_type: 'tenant',
      lease_id: '',
      customer_id: '',
      notes: '',
    },
  })

  const parkerType = watch('parker_type')
  const leaseIdValue = watch('lease_id')

  // Derive customer name from selected lease (for the read-only chip)
  const selectedLease = leases.find((l) => String(l.id) === leaseIdValue)
  const derivedCustomerName = selectedLease?.customer?.name

  // Populate form when editing
  useEffect(() => {
    if (isEditing && existingVehicle) {
      reset({
        plate_text: existingVehicle.plate_text ?? '',
        license_plate: existingVehicle.license_plate,
        vehicle_type: existingVehicle.vehicle_type,
        province: existingVehicle.province || '',
        brand: existingVehicle.brand || '',
        model: existingVehicle.model || '',
        color: existingVehicle.color || '',
        parker_type: (existingVehicle.parker_type as ParkerType) || 'tenant',
        lease_id: existingVehicle.lease_id ? String(existingVehicle.lease_id) : '',
        customer_id: existingVehicle.customer_id ? String(existingVehicle.customer_id) : '',
        notes: existingVehicle.notes || '',
      })
      // Pre-populate photo keys from existing vehicle data
      setFrontPhotoKey(existingVehicle.front_photo_key ?? null)
      setRearPhotoKey(existingVehicle.rear_photo_key ?? null)
    }
  }, [isEditing, existingVehicle, reset])

  // Count active permits (used for the edit-mode warning)
  const activePermitCount = existingVehicle?.active_permits?.length ?? 0

  const onSubmit = async (data: VehicleFormData) => {
    setSubmitAttempted(true)

    // Validate photo requirements on create (not required on edit if already present)
    if (!isEditing) {
      let hasError = false
      if (!frontPhotoKey) {
        setFrontPhotoError(t('vehicles.front_photo_required'))
        hasError = true
      }
      if (!rearPhotoKey) {
        setRearPhotoError(t('vehicles.rear_photo_required'))
        hasError = true
      }
      if (hasError) return
    }

    const isTenant = data.parker_type === 'tenant'
    const isExternal = data.parker_type === 'external'

    if (isEditing) {
      await updateVehicle.mutateAsync({
        id: vehicleId,
        license_plate: data.license_plate,
        vehicle_type: data.vehicle_type as VehicleType,
        province: data.province || undefined,
        brand: data.brand || undefined,
        model: data.model || undefined,
        color: data.color || undefined,
        parker_type: data.parker_type,
        lease_id: isTenant && data.lease_id ? Number(data.lease_id) : null,
        customer_id: isExternal && data.customer_id ? Number(data.customer_id) : null,
        notes: data.notes || undefined,
      })
      navigate(`/properties/${existingVehicle?.property_id}/parking/vehicles`)
    } else {
      if (!resolvedPropertyId) return
      await createVehicle.mutateAsync({
        property_id: resolvedPropertyId,
        plate_text: data.plate_text || undefined,
        license_plate: data.license_plate,
        vehicle_type: data.vehicle_type as VehicleType,
        province: data.province || undefined,
        brand: data.brand || undefined,
        model: data.model || undefined,
        color: data.color || undefined,
        // EP-139 T139-006. These two were omitted, behind a note claiming
        // CreateVehicleInput lacked the fields and that "the backend normalises
        // from customer_id presence on new records". Both halves were stale:
        // the input type carries `parker_type` and `lease_id` (useVehicles.ts),
        // and normalize_for_parker_type derives the FKs FROM parker_type, never
        // parker_type from an FK. So no parker_type reached the server, the
        // column default `tenant` applied, and the lease the form had just
        // REQUIRED the operator to pick was discarded on the way out. Every
        // tenant vehicle created here landed unlinked; 20 of the 21 vehicles in
        // production are in that state.
        parker_type: data.parker_type,
        lease_id: isTenant && data.lease_id ? Number(data.lease_id) : undefined,
        customer_id: isExternal && data.customer_id ? Number(data.customer_id) : undefined,
        notes: data.notes || undefined,
        front_photo_key: frontPhotoKey ?? undefined,
        rear_photo_key: rearPhotoKey ?? undefined,
      })
      navigate(`/properties/${resolvedPropertyId}/parking/vehicles`)
    }
  }

  if (isEditing && isLoadingVehicle) {
    return (
        <div className="space-y-6">
          <Card>
            <div className="h-64 animate-pulse bg-base-200 rounded" />
          </Card>
        </div>
    )
  }

  const propertyName = property?.name || (isEditing ? existingVehicle?.property_id?.toString() : propertyIdParam) || '...'
  const listHref = isEditing
    ? `/properties/${existingVehicle?.property_id}/parking/vehicles`
    : `/properties/${propertyIdParam}/parking/vehicles`

  // Sort leases by unit name with natural (numeric-aware) ordering so unit 2
  // appears before unit 10. The fallback lease (edit mode, outside page 1) is
  // always kept at the top of the list regardless of sort order.
  const sortedLeases = [...leases].sort((a, b) => {
    const aName = a.unit?.name ?? `#${a.unit_id}`
    const bName = b.unit?.name ?? `#${b.unit_id}`
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' })
  })

  const leaseOptions = [
    { value: '', label: `— ${tc('actions.select')} —` },
    ...sortedLeases.map((l) => ({
      value: String(l.id),
      label: `${l.lease_code} · ${l.unit?.name ?? `#${l.unit_id}`} · ${l.customer?.name ?? `#${l.customer_id}`}`,
    })),
  ]

  const customerOptions = [
    { value: '', label: '-' },
    ...customers.map((c) => {
      const name = c.last_name
        ? `${c.first_name ?? ''} ${c.last_name}`.trim()
        : (c.first_name ?? c.phone ?? `#${c.id}`)
      // Show source='external' badge for external customers
      const badge = (c as { source?: string }).source === 'external' ? ' [External]' : ''
      return { value: String(c.id), label: `${name}${badge}` }
    }),
  ]

  return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: propertyName, href: listHref },
            { label: isEditing ? t('vehicles.edit') : t('vehicles.new_vehicle') },
          ]}
        />

        <PageHeader
          title={isEditing ? t('vehicles.edit') : t('vehicles.new_vehicle')}
        />

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

            {/* Parker Type fieldset — EPV-9 */}
            <fieldset>
              <legend className="text-sm font-medium mb-2">{t('vehicles.parker_type.label')}</legend>

              {/* Active permits warning — edit mode only */}
              {isEditing && activePermitCount > 0 && (
                <Alert
                  variant="warning"
                  icon={null}
                  role="alert"
                  className="mb-3"
                  data-testid="active-permits-warning"
                >
                  <span>
                    {t('vehicles.active_permits_warning', { count: activePermitCount })}
                  </span>
                </Alert>
              )}

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Radio
                    variant="primary"
                    value="tenant"
                    {...register('parker_type')}
                    onChange={() => {
                      setValue('parker_type', 'tenant')
                      setValue('customer_id', '')
                    }}
                  />
                  <div>
                    <span className="font-medium">{t('vehicles.parker_type.tenant')}</span>
                    <p className="text-xs text-base-content/60">{t('vehicles.parker_type_hint.tenant')}</p>
                  </div>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <Radio
                    variant="primary"
                    value="external"
                    {...register('parker_type')}
                    onChange={() => {
                      setValue('parker_type', 'external')
                      setValue('lease_id', '')
                    }}
                  />
                  <div>
                    <span className="font-medium">{t('vehicles.parker_type.external')}</span>
                    <p className="text-xs text-base-content/60">{t('vehicles.parker_type_hint.external')}</p>
                  </div>
                </label>
              </div>
            </fieldset>

            {/* Tenant zone: Lease dropdown + read-only customer chip */}
            {parkerType === 'tenant' && (
              <div className="p-4 rounded-lg bg-base-200 space-y-3">
                <Controller
                  name="lease_id"
                  control={control}
                  // EP-139 T139-003. Required on CREATE only. The server
                  // deliberately tolerates a tenant vehicle with no lease:
                  // vehicle.rb's validate_tenant_fields opens
                  // `return unless lease_id.present?`, the EPV-1b backfill
                  // documents case (iv) as leaving such rows for the operator,
                  // and the parker_type check constraint says outright that the
                  // invariant "deliberately tolerates tenant vehicles without a
                  // lease (legacy/pre-EPV rows)". Requiring it here on a form
                  // shared by create and edit froze every one of those rows:
                  // changing the colour or the notes was refused too, and the
                  // form offers no `archived` option to escape through. Same
                  // shape as the model's own precedent one file over,
                  // `validates :front_photo_key, presence: true, on: :create`,
                  // written so legacy rows stay editable.
                  rules={{ required: isEditing ? false : t('validation.lease_required') }}
                  render={({ field }) => (
                    <SearchableDropdown
                      name="lease_id"
                      label={tc('nav.leases')}
                      options={leaseOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={`— ${tc('actions.select')} —`}
                      error={errors.lease_id?.message}
                      onSearch={handleLeaseSearch}
                      isLoading={isLoadingLeases}
                      onLoadMore={() => setLeasePage((p) => p + 1)}
                      hasMore={hasMoreLeases}
                    />
                  )}
                />

                {derivedCustomerName && (
                  <div data-testid="derived-customer-chip" className="text-sm text-base-content/70">
                    <span className="font-medium">{t('vehicles.customer_auto_derived')}: </span>
                    <Badge variant="ghost">{derivedCustomerName}</Badge>
                  </div>
                )}
              </div>
            )}

            {/* External zone: Customer dropdown with search */}
            {parkerType === 'external' && (
              <div className="p-4 rounded-lg bg-base-200">
                <Controller
                  name="customer_id"
                  control={control}
                  rules={{ required: t('validation.customer_required_for_external') }}
                  render={({ field }) => (
                    <SearchableDropdown
                      name="customer_id"
                      label={tc('nav.customers')}
                      options={customerOptions}
                      value={field.value}
                      onChange={field.onChange}
                      onSearch={setCustomerSearchQuery}
                      isLoading={isLoadingCustomers}
                      placeholder={tc('nav.customers')}
                      error={errors.customer_id?.message}
                      hasMore={
                        !!customersData?.meta &&
                        customersData.meta.current_page < customersData.meta.total_pages
                      }
                      onLoadMore={() => setCustomerPage((p) => p + 1)}
                    />
                  )}
                />
              </div>
            )}

            {/* P0-01: plate_text — free-text, required on create */}
            <Input
              label={t('vehicles.plate_text_label')}
              placeholder={t('vehicles.plate_text_placeholder')}
              error={errors.plate_text?.message}
              {...register('plate_text', {
                required: !isEditing ? t('validation.plate_text_required') : false,
              })}
            />

            <div className="grid gap-6 md:grid-cols-2">
              {/* License Plate */}
              <Input
                label={t('vehicles.license_plate')}
                placeholder={t('vehicles.license_plate_placeholder')}
                error={errors.license_plate?.message}
                {...register('license_plate', {
                  required: t('validation.license_plate_required'),
                })}
                onBlur={(e) => {
                  setValue('license_plate', e.target.value.toUpperCase())
                }}
              />

              {/* Vehicle Type */}
              <Select
                label={t('vehicles.vehicle_type')}
                error={errors.vehicle_type?.message}
                options={vehicleTypeOptions}
                {...register('vehicle_type', {
                  required: t('validation.vehicle_type_required'),
                  validate: (v) => v !== '' || t('validation.vehicle_type_required'),
                })}
              />

              {/* Province (Searchable Dropdown) */}
              <Controller
                name="province"
                control={control}
                render={({ field }) => (
                  <SearchableDropdown
                    label={t('vehicles.province')}
                    options={[{ value: '', label: '-' }, ...provinceOptions]}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('vehicles.province')}
                  />
                )}
              />

              {/* Brand */}
              <Input
                label={t('vehicles.brand')}
                placeholder={t('vehicles.brand')}
                {...register('brand')}
              />

              {/* Model */}
              <Input
                label={t('vehicles.model')}
                placeholder={t('vehicles.model')}
                {...register('model')}
              />

              {/* Color */}
              <Input
                label={t('vehicles.color')}
                placeholder={t('vehicles.color')}
                {...register('color')}
              />
            </div>

            {/* P0-01: Vehicle photos — required on create */}
            {!isEditing && (
              <div>
                <p className="text-sm font-medium mb-3">{t('vehicles.photos_section_label')}</p>
                <div className="grid gap-6 md:grid-cols-2">
                  <VehiclePhotoUpload
                    label={t('vehicles.front_photo_label')}
                    hint={t('vehicles.front_photo_hint')}
                    photoKey={frontPhotoKey}
                    uploading={frontPhotoUploading}
                    error={submitAttempted && !frontPhotoKey ? (frontPhotoError ?? t('vehicles.front_photo_required')) : frontPhotoError}
                    inputRef={frontPhotoInputRef}
                    onFileChange={handleFrontPhotoChange}
                    onRemove={() => setFrontPhotoKey(null)}
                    data-testid="front-photo-upload"
                  />

                  <VehiclePhotoUpload
                    label={t('vehicles.rear_photo_label')}
                    hint={t('vehicles.rear_photo_hint')}
                    photoKey={rearPhotoKey}
                    uploading={rearPhotoUploading}
                    error={submitAttempted && !rearPhotoKey ? (rearPhotoError ?? t('vehicles.rear_photo_required')) : rearPhotoError}
                    inputRef={rearPhotoInputRef}
                    onFileChange={handleRearPhotoChange}
                    onRemove={() => setRearPhotoKey(null)}
                    data-testid="rear-photo-upload"
                  />
                </div>
              </div>
            )}

            {/* Edit mode: show existing photos if available */}
            {isEditing && (frontPhotoKey || rearPhotoKey) && (
              <div>
                <p className="text-sm font-medium mb-3">{t('vehicles.photos_section_label')}</p>
                <div className="flex gap-4 flex-wrap">
                  {frontPhotoKey && (
                    <div className="space-y-1">
                      <p className="text-xs text-base-content/60">{t('vehicles.front_photo_label')}</p>
                      <img
                        src={vehiclePhotoUrl(frontPhotoKey)}
                        alt={t('vehicles.front_photo_label')}
                        className="w-32 h-24 object-cover rounded-lg"
                        data-testid="edit-front-photo"
                      />
                    </div>
                  )}
                  {rearPhotoKey && (
                    <div className="space-y-1">
                      <p className="text-xs text-base-content/60">{t('vehicles.rear_photo_label')}</p>
                      <img
                        src={vehiclePhotoUrl(rearPhotoKey)}
                        alt={t('vehicles.rear_photo_label')}
                        className="w-32 h-24 object-cover rounded-lg"
                        data-testid="edit-rear-photo"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            <Textarea
              label={t('vehicles.notes')}
              rows={3}
              {...register('notes')}
            />

            <FormActions>
              <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                {tc('actions.cancel')}
              </Button>
              <Button
                type="submit"
                loading={isEditing ? updateVehicle.isPending : createVehicle.isPending}
              >
                {tc('actions.save')}
              </Button>
            </FormActions>
          </form>
        </Card>
      </div>
  )
}
